<!-- eslint-disable max-len -->
<template>
  <div
    class="collaborator-display"
    v-if="draft && draft.pages"
  >
    <div
      class="collaborator-display__admins"
      :class="{'collaborator-display__admins--border-right': showSeparator}"
    >
      <ul class="collaborator-display__admins-list">
        <li
          class="collaborator-display__admins-list-item"
          v-for="owner in ownerDisplayed"
          :key="owner.id"
        >
          <NebulaTooltip
            class="collaborator-display__avatar-wrapper"
            position="bottom"
            :text="`${owner.firstName || ''} ${owner.lastName || ''}`"
          >
            <div
              class="collaborator-display__avatar"
              :style="{
                backgroundColor: owner.color,
                opacity: owner.connectionTime || '0.3', // disconnected owners are grayed out
              }"
              role="img"
              :aria-label="`${owner.firstName} ${owner.lastName} ${$t('Owner')}`"
            >
              <span
                aria-hidden="true"
                class="collaborator-display__initials"
              >{{ owner.initials }}</span>
            </div>
          </NebulaTooltip>
        </li>
        <li
          class="collaborator-display__admins-list-item"
          v-for="admin in adminsDisplayed"
          :key="admin.id"
        >
          <NebulaTooltip
            class="collaborator-display__avatar-wrapper"
            position="bottom"
            :text="`${admin.firstName || ''} ${admin.lastName || ''}`"
          >
            <div
              class="collaborator-display__avatar"
              :style="`background-color:${admin.color}`"
              role="img"
              :aria-label="`${admin.firstName} ${admin.lastName} ${$t('Admin')}`"
            >
              <span
                aria-hidden="true"
                class="collaborator-display__initials"
              >{{ admin.initials }}</span>
            </div>
          </NebulaTooltip>
        </li>
      </ul>
    </div>
    <div
      class="collaborator-display__editors"
      v-if="editorsDisplayed.length || truncatedCollaborators"
    >
      <ul
        class="collaborator-display__editors-list"
        role="none"
      >
        <li
          class="collaborator-display__editors-list-item"
          v-for="editor in editorsDisplayed"
          :key="editor.id"
        >
          <NebulaTooltip
            class="collaborator-display__avatar-wrapper"
            position="bottom"
            :text="`${editor.firstName || ''} ${editor.lastName || ''}`"
          >
            <div
              class="collaborator-display__avatar"
              :style="`background-color:${editor.color}`"
              role="img"
              :aria-label="`${editor.firstName} ${editor.lastName}`"
            >
              <span
                aria-hidden="true"
                class="collaborator-display__initials"
              >{{ editor.initials }}</span>
            </div>
          </NebulaTooltip>
        </li>
        <li class="collaborator-display__editors-list-item">
          <div
            class="collaborator-display__editor-list-button"
            v-if="truncatedCollaborators"
            tabindex="0"
            role="list"
            :aria-label="$t('Editors')"
          >
            <span
              aria-hidden="true"
              class="collaborator-display__editor-list-truncated"
            >+{{ truncatedCollaborators }}</span>
            <span class="comet-screenreader-only">
              <span
                v-for="owner in (connectedOwners.length ? connectedOwners : ownerDisplayed)"
                :key="owner.id"
                role="listitem"
              >
                {{ owner.firstName }} {{ owner.lastName }}
                {{ $t('Owner') }}
                {{ owner.count > 1 ? $t('%(connections)d connections', { connections: owner.count }) : '' }}
              </span>
              <span
                v-for="admin in connectedAdmins"
                :key="admin.id"
                role="listitem"
              >
                {{ admin.firstName }} {{ admin.lastName }}
                {{ $t('Admin') }}
                {{ admin.count > 1 ? $t('%(connections)d connections', { connections: admin.count }) : '' }}
              </span>
              <span
                v-for="editor in connectedEditors"
                :key="editor.id"
                role="listitem"
              >
                {{ editor.firstName }} {{ editor.lastName }}
                {{ editor.count > 1 ? $t('%(connections)d connections', { connections: editor.count }) : '' }}
              </span>
            </span>
          </div>
          <div
            class="collaborator-display__editor-list-popover comet-popover comet-popover--bottom"
            aria-describedby="collaborator-display__editor-list-popover-content"
          >
            <span class="collaborator-display__editor-list-popover-nub comet-popover__nub" />
            <div
              id="collaborator-display__editor-list-popover-content"
              class="collaborator-display__editor-list-popover-content"
            >
              <h3 class="collaborator-display__editor-list-popover-header">
                {{ collaboratorCount }} {{ $t('Editors') }}
              </h3>
              <div class="collaborator-display__editor-list-popover-lists">
                <ul class="collaborator-display__editor-list-popover-admins-list">
                  <li
                    class="collaborator-display__editor-list-popover-list-item"
                    v-for="owner in (connectedOwners.length ? connectedOwners : ownerDisplayed)"
                    :key="owner.id"
                  >
                    <div class="collaborator-display__avatar-name-group">
                      <div
                        class="collaborator-display__avatar"
                        :style="{
                          backgroundColor: owner.color,
                          opacity: owner.connectionTime || '0.3', // disconnected owners are grayed out
                        }"
                      >
                        <span class="collaborator-display__initials">{{ owner.initials }}</span>
                      </div>
                      <p class="collaborator-display__name">
                        {{ owner.firstName }} {{ owner.lastName }}
                        <span class="collaborator-display__pill">{{ $t('Owner') }}</span>
                        <span v-if="owner.count > 1"> ({{ owner.count }}) </span>
                      </p>
                    </div>
                  </li>
                  <li
                    class="collaborator-display__editor-list-popover-list-item"
                    v-for="admin in connectedAdmins"
                    :key="admin.id"
                  >
                    <div class="collaborator-display__avatar-name-group">
                      <div
                        class="collaborator-display__avatar"
                        :style="`background-color:${admin.color}`"
                      >
                        <span class="collaborator-display__initials">{{ admin.initials }}</span>
                      </div>
                      <p class="collaborator-display__name">
                        {{ admin.firstName }} {{ admin.lastName }}
                        <span class="collaborator-display__pill">{{ $t('Admin') }}</span>
                        <span v-if="admin.count > 1"> ({{ admin.count }}) </span>
                      </p>
                    </div>
                  </li>
                </ul>
                <ul class="collaborator-display__editor-list-popover-editors-list">
                  <li
                    class="collaborator-display__editor-list-popover-list-item"
                    v-for="editor in connectedEditors"
                    :key="editor.id"
                  >
                    <div class="collaborator-display__avatar-name-group">
                      <div
                        class="collaborator-display__avatar"
                        :style="`background-color:${editor.color}`"
                      >
                        <span class="collaborator-display__initials">{{ editor.initials }}</span>
                      </div>
                      <p class="collaborator-display__name">
                        {{ editor.firstName }} {{ editor.lastName }}
                        <span v-if="editor.count > 1"> ({{ editor.count }}) </span>
                      </p>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
import { NebulaTooltip } from '@discoveryedu/nebula-components';
import { mapState } from 'pinia';
import { get } from 'lodash-es';
import { toInitial } from '@/lib/utils';
import {
  useAppStore,
  useEditorStore,
} from '@/stores';

export default {
  name: 'CollaboratorDisplay',
  components: {
    NebulaTooltip,
  },
  computed: {
    ...mapState(useEditorStore, {
      draft: 'draft',
      connected: 'connectedUsers',
      usersColors: 'connectedUsersColors',
    }),
    ...mapState(useAppStore, [
      'loading',
    ]),
    connectedUsers() {
      return this.connected ? this.connected : [];
    },
    connectedUsersColors() {
      return this.usersColors ? this.usersColors : {};
    },
    ownerId() {
      return this.draft?.owner_guid;
    },
    shares() {
      return this.draft?.shares;
    },
    collaboratorCount() {
      return this.connectedUsers.length;
    },
    adminIdIndex() {
      const users = get(this.shares, 'user', []);
      return users
        .filter((user) => user.type === 'admin')
        .map((user) => user.owner_guid);
    },
    editorIdIndex() {
      const users = get(this.shares, 'user', []);
      return users
        .filter((user) => user.type === 'edit')
        .map((user) => user.owner_guid);
    },
    formattedConnectedUsers() {
      return this.aggregateConnections(
        this.connectedUsers.map((user) => {
          const initials = this.getInitials(user.firstName, user.lastName);
          const color = this.connectedUsersColors[user.clientId];
          return { ...user, initials, color };
        }),
      );
    },
    connectedOwners() {
      return this.formattedConnectedUsers.filter((user) => this.ownerId === user.clientId);
    },
    ownerDisplayed() {
      // If the owner is connected, we can find their info in this.connectedOwners and
      // display the first connection. Otherwise, we have to reconstruct it on our own
      return this.connectedOwners.length
        ? [this.connectedOwners[0]]
        : [{
          clientId: this.draft.owner_guid,
          color: this.connectedUsersColors[this.draft.owner_guid],
          connectionTime: null,
          count: 1,
          firstName: this.draft.owner_first_name,
          id: this.draft.owner_guid,
          initials: `${toInitial(this.draft.owner_first_name)}${toInitial(this.draft.owner_last_name)}`,
          isCurrentUser: false,
          isHost: false,
          lastName: this.draft.owner_last_name,
        }];
    },
    connectedAdmins() {
      return this.formattedConnectedUsers
        .filter((user) => this.adminIdIndex.includes(user.clientId));
    },
    adminsDisplayed() {
      let count = 0;
      return this.connectedAdmins.filter(() => {
        if (count <= 2) {
          count += 1;
          return true;
        }
        return false;
      });
    },
    connectedEditors() {
      // return this.formattedConnectedUsers
      //   .filter(user => this.editorIdIndex.includes(user.clientId));
      return this.formattedConnectedUsers /** Temporary fix until STUD-595 is implemented */
        .filter((user) => !this.adminIdIndex.includes(user.clientId))
        .filter((user) => user.clientId !== this.ownerId);
    },
    editorsDisplayed() {
      const vm = this;
      let count = 0;
      return this.connectedEditors.filter(() => {
        if (count <= 2
            && (count + vm.adminsDisplayed.length) <= 2) {
          count += 1;
          return true;
        }
        return false;
      });
    },
    truncatedCollaborators() {
      // We might be showing a disconnected owner, but we shouldn't include that
      // in our calculation
      const ownerCount = Math.min(1, this.connectedOwners.length);

      return this.connectedUsers.length
      - (ownerCount + this.adminsDisplayed.length + this.editorsDisplayed.length);
    },
    showSeparator() {
      // There will always be at least the owner displayed on the left side.
      // Show the separator if we're showing any additional editors on the right side.
      return this.editorsDisplayed.length || this.truncatedCollaborators;
    },
  },
  methods: {
    getInitials(firstName, lastName) {
      return `${firstName.charAt(0)}${lastName.charAt(0)}`;
    },
    aggregateConnections(connections) {
      const aggregateConnections = {};
      connections.forEach((connection) => {
        if (connection.clientId in aggregateConnections) {
          aggregateConnections[connection.clientId].count += 1;
        } else {
          aggregateConnections[connection.clientId] = {
            ...connection,
            count: 1,
          };
        }
      });
      return Object.values(aggregateConnections);
    },
  },
};
</script>

<style lang="stylus">
.collaborator-display {
  display: flex;
  margin-inline-end: $nebula-space-1x;

  &__admins--border-right {
    border-inline-end: $comet-border-hairline-default;
  }

  &__admins-list,
  &__editors-list, {
    display: flex;
    list-style: none;
    margin: 0;
    padding: 0;
    position: relative;
  }

  &__admins-list-item,
  &__editors-list-item {
    margin-inline-end: $nebula-space-1x;
    position: relative;
  }

  & &__avatar,
  & &__editor-list-button {
    align-items: center;
    border-radius: 50%;
    display: flex;
    flex-shrink: 0;
    height: 32px;
    justify-content: center;
    margin: 0;
    width: 32px;
  }

  & &__editor-list-button {
    border: $comet-border-hairline-default;
  }

  &__avatar-wrapper {
    display: block;
  }

  &__editor-list-truncated {
    color: $comet-text-color-secondary-on-dark;
  }

  &__initials {
    color: $comet-text-color-primary-on-dark;
    font-size: 0.90rem;
    line-height: 1;
    text-transform: uppercase;
  }

  &__editors {
    margin-inline-start: $nebula-space-1x;
  }

  & &__editor-list-popover {
    border-radius: $nebula-border-radius-badge-default;
    box-shadow: $comet-shadow-z-shadow-1;
    color: $comet-text-color-primary-default;
    display: none;
    inset-inline-end: 0;
    max-width: none;
    opacity: 1;
    padding-inline-end: 0;
    position: absolute;
    top: 150%;
    white-space: nowrap;
    visibility: visible;
    z-index: 600;
  }

  & &__editor-list-popover,
  & &__editor-list-popover-nub,
  & &__editor-list-popover-content {
    background: $comet-background-color-default;
  }

  & &__editor-list-popover &__editor-list-popover-nub {
    box-shadow: none;
    inset-inline-start: 90%;
  }

  &__editor-list-button:hover + &__editor-list-popover,
  &__editor-list-button:focus + &__editor-list-popover {
    display: block;
  }

  &__editor-list-popover-header {
    margin-top: $nebula-space-1x;
    padding-inline-end: $nebula-space-2x;
  }

  &__editor-list-popover-lists {
    max-height: calc(100vh - 194px);
    overflow-x: hidden;
    overflow-y: auto;
    padding-inline-end: $nebula-space-2x;
  }

  &__editor-list-popover-admins-list,
  &__editor-list-popover-editors-list {
    list-style: none;
    margin: 0;
    padding: 0;
  }

  &__editor-list-popover-admins-list {
    margin-bottom: $nebula-space-1x;
  }

  &__avatar-name-group {
    display: flex;
    margin-bottom: $nebula-space-1x;

    &--connected {
      opacity: 1;
    }
  }

  &__name {
    align-self: center;
    margin: 0 $nebula-space-1x;
  }

  &__pill {
    background: $comet-background-color-light;
    border-radius: 25px;
    font-size: $comet-font-size-metadata;
    padding: $nebula-space-half $nebula-space-1x;
  }
}
</style>
